import axios from 'axios';
import { redirectToPortalLogin } from './portalLogin';

// Настройка базового URL для API
const isLocalhost = (url: string) => /^(https?:\/\/)?(localhost|127\.0\.0\.1)(:\d+)?$/i.test(url);

export const resolveApiBaseUrl = (envUrl: string | undefined, origin: string): string => {
  const configured = envUrl?.trim();
  if (!configured) return origin;
  if (isLocalhost(configured)) {
    if (!isLocalhost(origin)) return origin;
    const configuredUrl = new URL(configured);
    const originUrl = new URL(origin);
    if (configuredUrl.port === originUrl.port && configuredUrl.hostname !== originUrl.hostname) {
      return origin;
    }
  }
  return configured;
};

const inferBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL?.trim();
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return resolveApiBaseUrl(envUrl, window.location.origin);
  }
  return envUrl || 'http://localhost:5000';
};

axios.defaults.baseURL = inferBaseUrl();
axios.defaults.withCredentials = true;

const AUTH_ENDPOINT_REGEX = /\/api\/auth\/(login|refresh|register)\b/i;

export const isAuthRequestUrl = (url: unknown): boolean => {
  if (typeof url !== 'string' || url.length === 0) {
    return false;
  }
  return AUTH_ENDPOINT_REGEX.test(url);
};

export const getStoredToken = (): string | null => {
  try {
    return localStorage.getItem('token');
  } catch {
    return null;
  }
};

/**
 * Добавляет токен аутентификации к URL (для превью/скачивания в новой вкладке)
 */
export const addTokenToUrl = (url: string): string => {
  const token = getStoredToken();
  if (!token) return url;
  
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}token=${encodeURIComponent(token)}`;
};

const getStoredRefreshToken = (): string | null => {
  try {
    return localStorage.getItem('refreshToken');
  } catch {
    return null;
  }
};

// Добавляем Authorization ко всем запросам, если есть токен
axios.interceptors.request.use((config) => {
  try {
    const baseUrlRaw =
      typeof config.baseURL === 'string'
        ? config.baseURL
        : typeof axios.defaults.baseURL === 'string'
          ? axios.defaults.baseURL
          : '';
    const baseUrl = baseUrlRaw.replace(/\/+$/, '');

    if (baseUrl.endsWith('/api') && typeof config.url === 'string') {
      if (config.url === '/api') {
        config.url = '/';
      }
      // Защита от двойного /api при REACT_APP_API_URL с суффиксом /api
      if (config.url.startsWith('/api/')) {
        config.url = config.url.replace(/^\/api\//, '/');
      }
    }

    const token = getStoredToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch {}
  return config;
});

// Ответы login/refresh: прокси генератора отдаёт { token, refreshToken }, portal-auth (на поддоменах
// nginx ведёт /api/auth/* прямо в него) — { data: { tokens: { accessToken, refreshToken } } } или { data: { accessToken } }.
const readRefreshTokens = (data: any): { accessToken: string | null; refreshToken: string | null } => {
  const access = data?.data?.tokens?.accessToken ?? data?.data?.accessToken ?? data?.tokens?.accessToken ?? data?.accessToken ?? data?.token;
  const refresh = data?.data?.tokens?.refreshToken ?? data?.tokens?.refreshToken ?? data?.refreshToken;
  return {
    accessToken: typeof access === 'string' && access.trim() ? access.trim() : null,
    refreshToken: typeof refresh === 'string' && refresh.trim() ? refresh.trim() : null
  };
};

export const storeAuthTokens = (data: any): string | null => {
  const { accessToken, refreshToken } = readRefreshTokens(data);
  if (!accessToken) return null;
  localStorage.setItem('token', accessToken);
  if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
  return accessToken;
};

// Функция для обновления токена: refresh token из localStorage или, если его нет, cookie портала.
async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = getStoredRefreshToken();
  try {
    const payload = refreshToken ? { refreshToken } : {};
    const response = await axios.post('/api/auth/refresh', payload, { withCredentials: true });
    return storeAuthTokens(response.data);
  } catch (error: any) {
    if (!refreshToken) return null;
    if (error?.response?.status === 401 || error?.response?.status === 403) {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      return null;
    }
    // Временный сбой refresh не должен превращать исходный 401 в выход.
    throw error;
  }
}

// Добавляем перехватчик для обработки ошибок с автообновлением токена
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const requestUrl = typeof originalRequest.url === 'string' ? originalRequest.url : '';
    const isAuthRequest = isAuthRequestUrl(requestUrl);
    
    // Если 401 и есть refresh token - пробуем обновить
    if (error.response?.status === 401 && !isAuthRequest && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const newToken = await refreshAccessToken();
      if (newToken) {
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axios(originalRequest);
      }
    }
    
    // Если обновление не помогло или нет refresh token
    if (error.response?.status === 401 && !isAuthRequest) {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      redirectToPortalLogin();
    }
    return Promise.reject(error);
  }
);

export interface ProfileUser {
  id: number;
  email: string;
  role: string;
  balance?: number;
  generation_limit?: number;
  [key: string]: unknown;
}

/**
 * Пользователь из ответа /api/auth/profile. Генератор отдаёт { user }, portal-auth (на поддоменах
 * nginx ведёт /api/auth/* прямо в него) — { success, data: { id, email, role, ... } }.
 * Нераспознанный ответ — null: его нельзя считать ни входом, ни выходом.
 */
export const readProfileUser = (payload: any): ProfileUser | null => {
  const candidates = [payload?.user, payload?.data?.user, payload?.data, payload];
  const user = candidates.find((item) => item && typeof item === 'object' && typeof item.role === 'string' && item.role);
  return user || null;
};

let restorePromise: Promise<boolean> | null = null;

/**
 * Единый вход портала. На поддоменах *.3a-fb.info вход приходит cookie портала (refresh_token
 * на .3a-fb.info), а localStorage у каждого поддомена свой. Если локального токена нет,
 * восстанавливаем его из cookie через /api/auth/refresh; без cookie (dev по IP) — false.
 */
export function restorePortalSession(): Promise<boolean> {
  if (getStoredToken()) return Promise.resolve(true);
  if (restorePromise) return restorePromise;
  restorePromise = axios.post('/api/auth/refresh', {}, { withCredentials: true })
    .then((response) => Boolean(storeAuthTokens(response.data)))
    .catch(() => false)
    .finally(() => { restorePromise = null; });
  return restorePromise;
}

/**
 * Генерация SSO URL для перехода на другой сервис портала
 * @param targetUrl - URL сервиса (например, http://31.172.73.71:5002)
 * @returns URL с SSO токенами
 */
export function generateSSOUrl(targetUrl: string): string {
  const url = new URL(targetUrl);
  return url.toString();
}

/**
 * POST-handoff SSO без передачи токенов через URL.
 */
export function startSSOHandoff(targetUrl: string, redirectPath = '/dashboard'): void {
  const token = getStoredToken();
  if (!token) {
    window.location.href = targetUrl;
    return;
  }

  const refreshToken = getStoredRefreshToken();
  const base = new URL(targetUrl);
  const action = new URL('/api/auth/sso/consume', base).toString();

  const form = document.createElement('form');
  form.method = 'POST';
  form.action = action;
  form.style.display = 'none';

  const addField = (name: string, value: string) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  };

  addField('accessToken', token);
  if (refreshToken) {
    addField('refreshToken', refreshToken);
  }
  addField('redirect', redirectPath);

  document.body.appendChild(form);
  form.submit();
}

export default axios;
